// src/services/userManagementService.js
// Service for managing practice users and roles

import { supabase } from '../lib/supabaseClient';

/**
 * Get all users in a practice
 */
export const getPracticeUsers = async (practiceId) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, full_name, role, created_at')
      .eq('practice_id', practiceId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, users: data || [] };
  } catch (error) {
    console.error('Error fetching users:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Invite a new user to the practice
 */
export const inviteUser = async (email, role, practiceId, invitedBy) => {
  try {
    // In production, this would send an actual email invite
    // For now, we'll create a pending user record
    
    const { data, error } = await supabase
      .from('user_invites')
      .insert([{
        email,
        role,
        practice_id: practiceId,
        invited_by: invitedBy,
        status: 'pending'
      }])
      .select();

    if (error) throw error;

    return { 
      success: true, 
      message: `Invitation sent to ${email}`,
      invite: data[0]
    };
  } catch (error) {
    console.error('Error inviting user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update user role
 */
export const updateUserRole = async (userId, newRole) => {
  try {
    const { error } = await supabase
      .from('users')
      .update({ role: newRole })
      .eq('id', userId);

    if (error) throw error;

    return { 
      success: true, 
      message: `User role updated to ${newRole}` 
    };
  } catch (error) {
    console.error('Error updating user role:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove user from practice (soft delete - set as inactive)
 */
export const removeUser = async (userId) => {
  try {
    const { error } = await supabase
      .from('users')
      .update({ active: false })
      .eq('id', userId);

    if (error) throw error;

    return { 
      success: true, 
      message: 'User removed from practice' 
    };
  } catch (error) {
    console.error('Error removing user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if user has permission for an action
 */
export const checkPermission = (userRole, requiredRole) => {
  const roleHierarchy = {
    'admin': 4,
    'manager': 3,
    'staff': 2,
    'read_only': 1
  };

  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
};

/**
 * Get role display info
 */
export const getRoleInfo = (role) => {
  const roleInfo = {
    'admin': {
      label: 'Admin',
      color: 'purple',
      description: 'Full access - Can manage users, workflows, and settings'
    },
    'manager': {
      label: 'Manager',
      color: 'blue',
      description: 'Can use workflows, view reports, no user management'
    },
    'staff': {
      label: 'Staff',
      color: 'teal',
      description: 'Can use workflows only'
    },
    'read_only': {
      label: 'Read-only',
      color: 'gray',
      description: 'View dashboards and reports only'
    }
  };

  return roleInfo[role] || roleInfo['staff'];
};